// frontend/src/components/public-info/sections/AnimatedStepFlow.tsx
import React, { useEffect, useRef, useState } from "react";
import SectionContainer from "../shared/SectionContainer";
import SectionTitle from "../shared/SectionTitle";
import "./AnimatedStepFlow.css";

export type StepFlowStep = {
  title: string;
  description?: string;
  icon?: React.ReactNode;
};

type AnimatedStepFlowProps = {
  id?: string;
  title: string;
  subtitle?: string;
  steps: StepFlowStep[];
  card?: boolean;
  compact?: boolean; // For 5+ steps in a single row
};

const AnimatedStepFlow: React.FC<AnimatedStepFlowProps> = ({
  id,
  title,
  subtitle,
  steps,
  card = true,
  compact = false,
}) => {
  const [visibleSteps, setVisibleSteps] = useState<Set<number>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = stepRefs.current.findIndex((ref) => ref === entry.target);
            if (index !== -1) {
              // Add a staggered delay based on index
              setTimeout(() => {
                setVisibleSteps((prev) => {
                  const next = new Set(prev);
                  next.add(index);
                  return next;
                });
              }, index * 150);
            }
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    stepRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [steps.length]);

  return (
    <SectionContainer id={id} card={card}>
      <SectionTitle title={title} subtitle={subtitle} />
      <div className={`ocf-pi-asf ${compact ? "ocf-pi-asf--compact" : ""}`} ref={containerRef}>
        {steps.map((s, idx) => (
          <div
            key={`${s.title}-${idx}`}
            ref={(el) => { stepRefs.current[idx] = el; }}
            className={`ocf-pi-asf-step ${visibleSteps.has(idx) ? "is-visible" : ""}`}
          >
            <div className="ocf-pi-asf-badge" aria-hidden>
              {s.icon ?? idx + 1}
            </div>
            <div className="ocf-pi-asf-body">
              <div className="ocf-pi-asf-number">Step {idx + 1}</div>
              <div className="ocf-pi-asf-title">{s.title}</div>
              {s.description && (
                <div className="ocf-pi-asf-desc">{s.description}</div>
              )}
            </div>
            {idx < steps.length - 1 && (
              <div
                className={`ocf-pi-asf-connector ${visibleSteps.has(idx) ? "is-visible" : ""}`}
                aria-hidden
              />
            )}
          </div>
        ))}
      </div>
    </SectionContainer>
  );
};

export default AnimatedStepFlow;
